import * as React from "react";

import { HttpErrorDto } from "@common/dto/http";
import { trDts } from "@modules/datasheets/utils/i18n";
import { GetDatasheetFolderFormats } from "@modules/datasheets/services/formats";

import { FormatFieldItem } from "@common/components/format-field-selector";
import { FolderPrimaryListDto } from "@common/dto/folder/primary-list-dto";

export type DatasheetFormatManagementFolder = Partial<FolderPrimaryListDto>;

export type DatasheetFormatManagementProps = {
  getFolders: () => Promise<DatasheetFormatManagementFolder[]>;
  getFormats: (params: GetDatasheetFolderFormats) => Promise<Array<FormatFieldItem>>;
  onSubmit: (params: { format: FormatFieldItem; folder: DatasheetFormatManagementFolder }) => void;
  showErrorMessage: (message: string) => void;
};

export const useDatasheetFormatManagementController = ({
  onSubmit,
  getFolders,
  getFormats,
  showErrorMessage,
}: DatasheetFormatManagementProps) => {
  const [folders, setFolders] = React.useState<Array<DatasheetFormatManagementFolder>>([]);
  const [folderFormats, setFolderFormats] = React.useState<Array<FormatFieldItem>>([]);

  const [isFoldersLoading, setIsFoldersLoading] = React.useState(false);
  const [isFolderFormatsLoading, setIsFolderFormatsLoading] = React.useState(false);

  const [selectedFolder, setSelectedFolder] = React.useState<DatasheetFormatManagementFolder | undefined>();
  const [selectedFormat, setSelectedFormat] = React.useState<FormatFieldItem | undefined>();

  const handleGetFolders = React.useCallback(async () => {
    try {
      setIsFoldersLoading(true);
      const folders = await getFolders();
      setFolders(folders);
    } catch (err) {
      console.error(err);

      const errorMessage = (err as HttpErrorDto)?.entity || trDts("Failed to get folders");
      showErrorMessage(errorMessage);
    } finally {
      setIsFoldersLoading(false);
    }
  }, [showErrorMessage, getFolders]);

  const handleGetFormats = React.useCallback(
    async (params: GetDatasheetFolderFormats) => {
      try {
        setIsFolderFormatsLoading(true);
        const folderFormats = await getFormats(params);
        setFolderFormats(folderFormats);
      } catch (err) {
        console.error(err);

        const errorMessage = (err as HttpErrorDto)?.entity || trDts("Failed to get formats");
        showErrorMessage(errorMessage);
      } finally {
        setIsFolderFormatsLoading(false);
      }
    },
    [showErrorMessage, getFormats]
  );

  const handleSelectedFolderChange = React.useCallback(
    (folder: DatasheetFormatManagementFolder) => {
      setSelectedFolder(folder);
      handleGetFormats({ folderId: folder.id! });
    },
    [handleGetFormats]
  );

  const handleFormatSelect = React.useCallback(
    (format: FormatFieldItem) => {
      setSelectedFormat(format);

      onSubmit({ folder: selectedFolder!, format });
    },
    [onSubmit, selectedFolder]
  );

  React.useEffect(() => {
    handleGetFolders();
  }, []);

  return {
    folders,
    folderFormats,
    isFoldersLoading,
    isFolderFormatsLoading,
    selectedFolder,
    selectedFormat,
    handleSelectedFolderChange,
    handleFormatSelect,
  };
};
